/**
 * Desenho dos focos em WebGL: um `gl.POINTS` por foco, com a chama (SDF
 * analítica de flame.ts), contorno e brilho calculados no shader. Os vértices (posição, data, classe) sobem
 * para a GPU uma vez por conjunto de dados; a cada quadro do loop só mudam
 * os uniforms (instante atual, janela, estilo) e desenha-se a faixa
 * contígua de índices da janela de tempo (dados ordenados por data).
 *
 * O canvas usa `preserveDrawingBuffer: true`, então a exportação pode ler o
 * canvas (drawImage) a qualquer momento depois do render.
 */
import type { FireDataset } from './dataset';
import { FLAME_EXTENT, FLAME_GLSL, FLAME_INNER_MIN_PX, FLAME_INNER_WHITE } from './flame';
import { FADE, TRAIL_LOOKS } from './look';
import {
  FADE_FIRST,
  FADE_LEVELS,
  LEVEL_CURRENT,
  LEVEL_NORMAL,
  LEVEL_TRAIL_1,
  LEVEL_TRAIL_2,
  type VisibleWindow,
} from './timeFilter';
import type { FireStyle } from './types';

/** Número GLSL (sempre com ponto decimal). */
const g = (n: number) => (Number.isInteger(n) ? `${n}.0` : String(n));

/** Aparência do quadro atual e do rastro — gerada da mesma tabela do canvas 2D (look.ts). */
const TRAIL_GLSL = TRAIL_LOOKS.map(
  (l, i) =>
    `${i ? 'else ' : ''}if (level < ${g(i + 0.5)}) { scale = ${g(l.scale)}; alpha = ${g(l.alpha)}; glow = ${g(l.glow)}; ` +
    `glowAlpha = goes ? ${g(l.glowAlphaGoes)} : ${g(l.glowAlphaOthers)}; white = ${g(l.white)}; }`,
).join('\n  ');

/** Fontes dos shaders (exportadas para os testes). */
export const VERTEX = `
precision highp float;
attribute vec2 a_pos;
attribute float a_t;
attribute float a_cls;
uniform mat3 u_toClip;
uniform float u_now;
uniform float u_span;
uniform float u_hlFrom;
uniform float u_hasHl;
uniform float u_fade;
uniform float u_trail;
uniform float u_trail1From;
uniform float u_trail2From;
uniform float u_radius;
uniform float u_maxPoint;
varying float v_rc;
varying float v_glow;
varying float v_alpha;
varying float v_glowAlpha;
varying float v_cls;
varying float v_white;
varying float v_half;
void main() {
  vec3 p = u_toClip * vec3(a_pos, 1.0);
  gl_Position = vec4(p.xy, 0.0, 1.0);
  // Nível visual — mesma regra de ageLevel() (timeFilter.ts) e levelLook() (look.ts).
  float level;
  if (u_hasHl > 0.5 && a_t > u_hlFrom) level = ${g(LEVEL_CURRENT)};
  else if (u_fade < 0.5) level = ${g(LEVEL_NORMAL)};
  else if (u_trail > 0.5 && a_t > u_trail1From) level = ${g(LEVEL_TRAIL_1)};
  else if (u_trail > 0.5 && a_t > u_trail2From) level = ${g(LEVEL_TRAIL_2)};
  else {
    float frac = u_span > 0.0 ? (u_now - a_t) / u_span : 1.0;
    level = ${g(FADE_FIRST)} + clamp(floor(frac * ${g(FADE_LEVELS)}), 0.0, ${g(FADE_LEVELS - 1)});
  }
  bool goes = a_cls < 0.5;
  float scale; float alpha; float glow; float glowAlpha; float white;
  ${TRAIL_GLSL}
  else if (level > ${g(LEVEL_NORMAL - 0.5)}) {
    scale = 1.0; alpha = ${g(FADE.alpha)}; glow = goes ? ${g(FADE.glowGoes)} : 0.0; glowAlpha = ${g(FADE.glowAlpha)}; white = 0.0;
  } else {
    float k = (level - ${g(FADE_FIRST)}) / ${g(Math.max(1, FADE_LEVELS - 1))};
    scale = 1.0 - ${g(FADE.scaleDrop)} * k; alpha = ${g(FADE.alpha)} - ${g(FADE.alphaDrop)} * k;
    glow = goes ? ${g(FADE.glowGoes)} : 0.0; glowAlpha = ${g(FADE.glowAlpha)} * (1.0 - k); white = 0.0;
  }
  float rc = u_radius * scale;
  // A chama é mais alta que o antigo círculo: o ponto cobre a chama ou o brilho.
  float size = min(2.0 * (rc * max(glow, ${g(FLAME_EXTENT)}) + 1.5), u_maxPoint);
  gl_PointSize = size;
  float hs = size * 0.5;
  v_half = hs;
  v_rc = rc / hs;
  v_glow = rc * glow / hs;
  v_alpha = alpha;
  v_glowAlpha = glowAlpha;
  v_cls = a_cls;
  v_white = white;
}`;

export const FRAGMENT = `
precision mediump float;
uniform vec3 u_colorGoes;
uniform vec3 u_colorOthers;
uniform float u_opacity;
uniform float u_lineWidth;
uniform float u_minOutline;
uniform float u_pixelRatio;
varying float v_rc;
varying float v_glow;
varying float v_alpha;
varying float v_glowAlpha;
varying float v_cls;
varying float v_white;
varying float v_half;
${FLAME_GLSL}
void main() {
  vec2 d = gl_PointCoord * 2.0 - 1.0;
  float r = length(d);
  float aa = 0.75 / v_half;
  vec3 base = v_cls < 0.5 ? u_colorGoes : u_colorOthers;
  vec3 coreCol = mix(base, vec3(1.0), v_white);
  vec4 c = vec4(0.0);
  if (v_glow > 0.0) {
    float g = v_glowAlpha * (1.0 - smoothstep(v_rc * 0.6, v_glow, r));
    c = vec4(base, 1.0) * g;
  }
  // Chama: coordenadas em unidades do raio, y para cima (gl_PointCoord cresce para baixo).
  vec2 q = vec2(d.x, -d.y) / v_rc;
  float sd = flameOuter(q) * v_rc;
  float coreA = 1.0 - smoothstep(-aa, aa, sd);
  if (v_rc * v_half >= ${g(FLAME_INNER_MIN_PX)} * u_pixelRatio) {
    float innerA = 1.0 - smoothstep(-aa, aa, flameInner(q) * v_rc);
    coreCol = mix(coreCol, mix(coreCol, vec3(1.0), ${g(FLAME_INNER_WHITE)}), innerA);
  }
  vec4 core = vec4(coreCol, 1.0) * coreA;
  c = core + c * (1.0 - core.a);
  if (v_rc * v_half >= u_minOutline) {
    float lw = u_lineWidth / v_half;
    float ring = 1.0 - smoothstep(lw * 0.5 - aa, lw * 0.5 + aa, abs(sd));
    float ra = 0.55 * ring;
    c = vec4(0.0, 0.0, 0.0, ra) + c * (1.0 - ra);
  }
  c *= v_alpha * u_opacity;
  if (c.a < 0.003) discard;
  gl_FragColor = c;
}`;

const FLOATS_PER_POINT = 4;

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', '').padEnd(6, '0').slice(0, 6), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export interface GlDrawParams {
  dataset: FireDataset;
  window: VisibleWindow;
  style: FireStyle;
  /** Raio base da chama em px CSS: `style.size` já multiplicado pelo fator do zoom (`map/escalaDoZoom.ts`). */
  size: number;
  /** Transformação coordenada do mapa → px CSS (frameState.coordinateToPixelTransform). */
  toPixel: ArrayLike<number>;
  /** Tamanho do canvas em px do dispositivo. */
  width: number;
  height: number;
  pixelRatio: number;
  /** Opacidade da camada (0–1). */
  opacity: number;
  lo: number;
  hi: number;
}

/**
 * Chave do desenho: tudo que muda os pixels. Se for igual à do último
 * desenho, o canvas (com `preserveDrawingBuffer`) já mostra o resultado e o
 * desenho é pulado — no modo "Todo o período" (estático) só há um desenho.
 * O instante atual só entra quando há esmaecimento (modo acumulado).
 */
export function glDrawKey(p: GlDrawParams): string {
  const w = p.window;
  const m = p.toPixel;
  return [
    p.dataset.version,
    p.lo,
    p.hi,
    w.highlightFrom ?? 'n',
    w.fade ? `${w.now}/${w.span}` : '-',
    w.trail ? w.step : '-',
    p.size,
    p.style.colorGoes,
    p.style.colorOthers,
    p.opacity,
    m[0], m[1], m[2], m[3], m[4], m[5],
    p.width,
    p.height,
    p.pixelRatio,
  ].join('|');
}

/** Cria o contexto WebGL (ou null se o navegador não oferece). */
export function createGlContext(canvas: HTMLCanvasElement): WebGLRenderingContext | null {
  try {
    return canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: true,
      preserveDrawingBuffer: true,
      antialias: false,
      depth: false,
      stencil: false,
    });
  } catch {
    return null;
  }
}

export class GlPoints {
  private readonly gl: WebGLRenderingContext;
  private program: WebGLProgram | null = null;
  private buffer: WebGLBuffer | null = null;
  private uploaded: FireDataset | null = null;
  private origin: [number, number, number] = [0, 0, 0];
  private loc: Record<string, WebGLUniformLocation | null> = {};
  private attr = { pos: -1, t: -1, cls: -1 };
  private maxPoint = 64;

  constructor(gl: WebGLRenderingContext) {
    this.gl = gl;
    this.init();
  }

  private init() {
    const gl = this.gl;
    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
        throw new Error(`Shader dos focos: ${gl.getShaderInfoLog(sh)}`);
      }
      return sh;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS) && !gl.isContextLost()) {
      throw new Error(`Programa dos focos: ${gl.getProgramInfoLog(prog)}`);
    }
    this.program = prog;
    for (const name of [
      'u_toClip', 'u_now', 'u_span', 'u_hlFrom', 'u_hasHl', 'u_fade', 'u_trail', 'u_trail1From', 'u_trail2From',
      'u_radius', 'u_maxPoint',
      'u_colorGoes', 'u_colorOthers', 'u_opacity', 'u_lineWidth', 'u_minOutline', 'u_pixelRatio',
    ]) {
      this.loc[name] = gl.getUniformLocation(prog, name);
    }
    this.attr = {
      pos: gl.getAttribLocation(prog, 'a_pos'),
      t: gl.getAttribLocation(prog, 'a_t'),
      cls: gl.getAttribLocation(prog, 'a_cls'),
    };
    this.buffer = gl.createBuffer();
    const range = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as Float32Array | null;
    this.maxPoint = range ? range[1]! : 64;
    this.uploaded = null;
  }

  /** Sobe os vértices do conjunto (só quando ele muda). */
  private upload(ds: FireDataset) {
    const gl = this.gl;
    const n = ds.n;
    const ox = n ? ds.x[0]! : 0;
    const oy = n ? ds.y[0]! : 0;
    const t0 = n ? ds.t[0]! : 0;
    const data = new Float32Array(Math.max(1, n) * FLOATS_PER_POINT);
    for (let i = 0; i < n; i++) {
      const k = i * FLOATS_PER_POINT;
      data[k] = ds.x[i]! - ox;
      data[k + 1] = ds.y[i]! - oy;
      data[k + 2] = (ds.t[i]! - t0) / 60_000;
      data[k + 3] = ds.cls[i]!;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    this.origin = [ox, oy, t0];
    this.uploaded = ds;
  }

  draw(p: GlDrawParams): void {
    const gl = this.gl;
    if (gl.isContextLost()) return;
    if (this.uploaded !== p.dataset) this.upload(p.dataset);

    gl.viewport(0, 0, p.width, p.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const count = p.hi - p.lo;
    if (count <= 0) return;

    gl.useProgram(this.program);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    const stride = FLOATS_PER_POINT * 4;
    gl.enableVertexAttribArray(this.attr.pos);
    gl.vertexAttribPointer(this.attr.pos, 2, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(this.attr.t);
    gl.vertexAttribPointer(this.attr.t, 1, gl.FLOAT, false, stride, 8);
    gl.enableVertexAttribArray(this.attr.cls);
    gl.vertexAttribPointer(this.attr.cls, 1, gl.FLOAT, false, stride, 12);

    // (x − ox, y − oy) → px CSS → px do dispositivo → clip (composição em float64).
    const [a, b, c, d, e, f] = p.toPixel as unknown as [number, number, number, number, number, number];
    const [ox, oy, t0] = this.origin;
    const sx = (2 * p.pixelRatio) / p.width;
    const sy = (-2 * p.pixelRatio) / p.height;
    gl.uniformMatrix3fv(this.loc.u_toClip!, false, [
      sx * a, sy * b, 0,
      sx * c, sy * d, 0,
      sx * (a * ox + c * oy + e) - 1, sy * (b * ox + d * oy + f) + 1, 1,
    ]);
    const w = p.window;
    const toMin = (t: number) => (t - t0) / 60_000;
    gl.uniform1f(this.loc.u_now!, toMin(w.now));
    gl.uniform1f(this.loc.u_span!, w.span / 60_000);
    gl.uniform1f(this.loc.u_hlFrom!, w.highlightFrom === null ? 0 : toMin(w.highlightFrom));
    gl.uniform1f(this.loc.u_hasHl!, w.highlightFrom === null ? 0 : 1);
    gl.uniform1f(this.loc.u_fade!, w.fade ? 1 : 0);
    // Rastro: (atual − 2 passos, atual − passo] e (atual − 3 passos, atual − 2 passos].
    gl.uniform1f(this.loc.u_trail!, w.trail ? 1 : 0);
    gl.uniform1f(this.loc.u_trail1From!, toMin(w.now - 2 * w.step));
    gl.uniform1f(this.loc.u_trail2From!, toMin(w.now - 3 * w.step));
    gl.uniform1f(this.loc.u_radius!, Math.max(0.75, p.size) * p.pixelRatio);
    gl.uniform1f(this.loc.u_maxPoint!, this.maxPoint);
    gl.uniform3fv(this.loc.u_colorGoes!, hexToRgb(p.style.colorGoes));
    gl.uniform3fv(this.loc.u_colorOthers!, hexToRgb(p.style.colorOthers));
    gl.uniform1f(this.loc.u_opacity!, p.opacity);
    gl.uniform1f(this.loc.u_lineWidth!, Math.max(1, 0.8 * p.pixelRatio));
    gl.uniform1f(this.loc.u_minOutline!, 1.5 * p.pixelRatio);
    gl.uniform1f(this.loc.u_pixelRatio!, p.pixelRatio);

    gl.drawArrays(gl.POINTS, p.lo, count);
  }

  /** Após perda/restauração do contexto: recria programa e buffers. */
  restore(): void {
    this.init();
  }

  dispose(): void {
    const gl = this.gl;
    if (this.buffer) gl.deleteBuffer(this.buffer);
    if (this.program) gl.deleteProgram(this.program);
    this.buffer = null;
    this.program = null;
    this.uploaded = null;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
